/*
 * A `react` stand-in that is guaranteed to export `useId`.
 *
 * webpack is aliased to this module *only* for
 * `@amzn/core-grc-global-nav-frontend` (see the rule-scoped alias in
 * craco.config.js). That package statically does `import { useId } from 'react'`,
 * and on React 16 webpack rejects the build with:
 *
 *   Attempted import error: 'useId' is not exported from 'react'
 *
 * That is a compile-time check, so the runtime patch in `reactUseIdShim` cannot
 * satisfy it — hence this second, static adapter over the same fallback hook.
 * Jest needs no alias, because Babel compiles the import into a property read that
 * the runtime patch already covers.
 *
 * Exports are listed explicitly rather than star-re-exported, because `export *`
 * from a CommonJS module leaves webpack unable to verify the very imports this
 * module exists to guarantee. The list covers what the global-nav package imports;
 * if a future version reaches for another React API, the build fails loudly here
 * instead of breaking at runtime.
 *
 * Delete this module, `reactUseIdShim`, and the craco alias once the app is on
 * React 18 and `useId` is real.
 */
import * as React from 'react';
import useIdFallback from './reactUseIdShim';

export default React;

export {
  Children,
  Component,
  Fragment,
  PureComponent,
  Suspense,
  createContext,
  createElement,
  forwardRef,
  isValidElement,
  lazy,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

/*
 * Probed through `require` rather than the namespace import above: webpack also
 * statically validates property access on a namespace import, so `React.useId` would
 * reintroduce the very "Attempted import error" this module exists to avoid.
 */
// eslint-disable-next-line @typescript-eslint/no-var-requires
const reactRuntime: { useId?: () => string } = require('react');

/** React 18's `useId` when it exists, otherwise the counter-based fallback. */
export const useId: () => string = reactRuntime.useId ?? useIdFallback;
